import { Grid } from '../../core/Grid.js';
import { splitEntries } from '../stateSerialization.js';
import {
    Position,
    positionKey,
    parsePositionKey
} from '../../core/GridGeometry.js';
import { ScoreBreakdown, calculateScore } from '../../core/Scoring.js';
import type { ScoringHighlight } from '../scoringRules.js';

// All possible knight move offsets
const KNIGHT_MOVES: [number, number][] = [
    [-2, -1], [-2, 1], [-1, -2], [-1, 2],
    [1, -2], [1, 2], [2, -1], [2, 1]
];

/**
 * Create a normalized segment key from two positions.
 * Ensures the same key regardless of order.
 */
export function segmentKey(a: Position, b: Position): string {
    const keyA = positionKey(a);
    const keyB = positionKey(b);
    return keyA < keyB ? `${keyA}-${keyB}` : `${keyB}-${keyA}`;
}

/**
 * Parse a segment key back into two positions.
 */
export function parseSegmentKey(key: string): [Position, Position] {
    const [keyA, keyB] = key.split('-');
    const [rowA, colA] = keyA.split(',').map(Number);
    const [rowB, colB] = keyB.split(',').map(Number);
    return [
        { row: rowA, col: colA },
        { row: rowB, col: colB }
    ];
}

/** One connected component of the path, walked end to end. */
interface ChainWalk {
    keys: string[];
    isCycle: boolean;
}

/** A place where the path directly joins two digits that aren't cycle-consecutive. */
export interface BadJoin {
    digits: [number, number];
    segments: string[];
}

export class KnightGame {
    protected grid: Grid;
    protected selectedCell: Position | null = null;
    protected segments: Set<string> = new Set();
    protected visitedCells: Set<string> = new Set();
    protected digitsReached: Set<number> = new Set();

    protected get gs() { return this.grid.serializer; }

    constructor(grid: Grid) {
        this.grid = grid;
    }

    getGrid(): Grid {
        return this.grid;
    }

    getSelectedCell(): Position | null {
        return this.selectedCell;
    }

    getSegments(): Set<string> {
        return new Set(this.segments);
    }

    getVisitedCells(): Set<string> {
        return new Set(this.visitedCells);
    }

    getDigitsReached(): number[] {
        return Array.from(this.digitsReached).sort((a, b) => a - b);
    }

    isStarted(): boolean {
        return this.segments.size > 0;
    }

    /**
     * Build an adjacency map from all segments.
     * Maps each cell key to an array of connected cell keys.
     */
    protected buildAdjacencyMap(): Map<string, string[]> {
        const adjacency = new Map<string, string[]>();

        for (const seg of this.segments) {
            const [a, b] = parseSegmentKey(seg);
            const keyA = positionKey(a);
            const keyB = positionKey(b);

            if (!adjacency.has(keyA)) adjacency.set(keyA, []);
            if (!adjacency.has(keyB)) adjacency.set(keyB, []);
            adjacency.get(keyA)!.push(keyB);
            adjacency.get(keyB)!.push(keyA);
        }

        return adjacency;
    }

    /**
     * Get all cells connected to the given position via segments.
     */
    getConnectedCells(pos: Position): Position[] {
        const key = positionKey(pos);
        const neighbors = this.buildAdjacencyMap().get(key) || [];

        return neighbors.map(k => {
            const [row, col] = k.split(',').map(Number);
            return { row, col };
        });
    }

    /**
     * Check if a position is an endpoint (0 or 1 connections).
     */
    isEndpoint(pos: Position): boolean {
        return this.getConnectedCells(pos).length <= 1;
    }

    /**
     * Get all endpoints (cells with 0 or 1 connections).
     */
    getEndpoints(): Position[] {
        const endpoints: Position[] = [];
        for (const key of this.visitedCells) {
            const [row, col] = key.split(',').map(Number);
            const pos = { row, col };
            if (this.isEndpoint(pos)) {
                endpoints.push(pos);
            }
        }
        return endpoints;
    }

    /**
     * Traverse from an endpoint collecting digits in order.
     * Returns up to `limit` digits found along the chain.
     */
    private getChainDigitsFromEndpoint(endpoint: Position, limit: number): number[] {
        const adjacency = this.buildAdjacencyMap();
        const visited = new Set<string>();
        const digits: number[] = [];
        let currentKey: string | null = positionKey(endpoint);

        while (currentKey && digits.length < limit) {
            if (visited.has(currentKey)) break;
            visited.add(currentKey);

            const [row, col] = currentKey.split(',').map(Number);
            const digit = this.grid.getDigitAt({ row, col });
            if (digit !== null) {
                digits.push(digit);
            }

            const neighbors: string[] = adjacency.get(currentKey) || [];
            currentKey = neighbors.find((n: string) => !visited.has(n)) || null;
        }

        return digits;
    }

    /**
     * Get the closest digit to an endpoint by traversing its path chain.
     * Returns the digit found, or null if no digit in this chain.
     */
    getEndpointDigit(endpoint: Position): number | null {
        const digits = this.getChainDigitsFromEndpoint(endpoint, 1);
        return digits.length > 0 ? digits[0] : null;
    }

    /**
     * Get all valid next digits for this endpoint.
     * For single-digit chains, both directions are valid.
     * Determines direction by looking at the first two digits from the endpoint.
     */
    getNextDigitsForEndpoint(endpoint: Position): number[] {
        const digits = this.getChainDigitsFromEndpoint(endpoint, 2);

        if (digits.length === 0) return [];

        if (digits.length === 1) {
            // Single digit: can go either direction
            const d = digits[0];
            const below = this.grid.prevDigit(d);
            const above = this.grid.nextDigit(d);
            return [below, above];
        }

        // Two digits: determine direction from d1 → d2
        const d1 = digits[0]; // First digit (closest to endpoint)
        const d2 = digits[1]; // Second digit (next in chain)

        // Determine if d1→d2 is increasing or decreasing (with wrap)
        // Increasing: d2 = d1 + 1, or d1=max and d2=min (wrap)
        // Decreasing: d2 = d1 - 1, or d1=min and d2=max (wrap)
        let isIncreasing: boolean;
        if (d1 === this.grid.maxDigit && d2 === this.grid.minDigit) {
            isIncreasing = true;
        } else if (d1 === this.grid.minDigit && d2 === this.grid.maxDigit) {
            isIncreasing = false;
        } else {
            isIncreasing = d2 > d1;
        }

        // To extend before d1:
        // If chain is increasing (d1→d2 = +1), extend with d1-1
        // If chain is decreasing (d1→d2 = -1), extend with d1+1
        if (isIncreasing) {
            return [this.grid.prevDigit(d1)];
        } else {
            return [this.grid.nextDigit(d1)];
        }
    }

    /**
     * Get the "next" digit that should be reached from this endpoint.
     * Returns the first valid next digit, or null if none.
     */
    getNextDigitForEndpoint(endpoint: Position): number | null {
        const digits = this.getNextDigitsForEndpoint(endpoint);
        return digits.length > 0 ? digits[0] : null;
    }

    /**
     * Split the path into its connected components, each walked in order.
     * Every cell has at most 2 connections, so a component is always either a
     * simple path (walked from one of its two ends) or a simple cycle.
     */
    private getChainWalks(): ChainWalk[] {
        const adjacency = this.buildAdjacencyMap();
        const seen = new Set<string>();
        const walks: ChainWalk[] = [];

        for (const [key, neighbors] of adjacency) {
            if (neighbors.length === 1 && !seen.has(key)) {
                walks.push({ keys: this.walkChainFrom(key, adjacency, seen), isCycle: false });
            }
        }

        // Components with no end left to start from are cycles.
        for (const key of adjacency.keys()) {
            if (!seen.has(key)) {
                walks.push({ keys: this.walkChainFrom(key, adjacency, seen), isCycle: true });
            }
        }

        return walks;
    }

    private walkChainFrom(startKey: string, adjacency: Map<string, string[]>, seen: Set<string>): string[] {
        const keys: string[] = [];
        let current: string | null = startKey;

        while (current && !seen.has(current)) {
            seen.add(current);
            keys.push(current);
            current = (adjacency.get(current) || []).find(n => !seen.has(n)) || null;
        }

        return keys;
    }

    private areCycleConsecutive(a: number, b: number): boolean {
        return this.grid.nextDigit(a) === b || this.grid.prevDigit(a) === b;
    }

    /** Segments linking consecutive cells of a walk slice (teleport links have none). */
    private segmentsAlong(keys: string[]): string[] {
        const found: string[] = [];
        for (let i = 0; i + 1 < keys.length; i++) {
            const key = segmentKey(parsePositionKey(keys[i]), parsePositionKey(keys[i + 1]));
            if (this.segments.has(key)) found.push(key);
        }
        return found;
    }

    /**
     * Find every section of path running directly between two digits that aren't
     * cycle-consecutive. Extending a chain is already constrained to the next
     * digit, so only joining two chains can produce one of these.
     */
    findBadJoins(): BadJoin[] {
        const bad: BadJoin[] = [];

        for (const walk of this.getChainWalks()) {
            const digitIndices = walk.keys
                .map((key, i) => ({ digit: this.grid.getDigitAt(parsePositionKey(key)), i }))
                .filter((e): e is { digit: number, i: number } => e.digit !== null);

            const runs: Array<[{ digit: number, i: number }, { digit: number, i: number }, string[]]> = [];
            for (let n = 0; n + 1 < digitIndices.length; n++) {
                const [from, to] = [digitIndices[n], digitIndices[n + 1]];
                runs.push([from, to, walk.keys.slice(from.i, to.i + 1)]);
            }

            // A cycle also joins its last digit back around to its first.
            if (walk.isCycle && digitIndices.length >= 2) {
                const [first, last] = [digitIndices[0], digitIndices[digitIndices.length - 1]];
                runs.push([last, first, walk.keys.slice(last.i).concat(walk.keys.slice(0, first.i + 1))]);
            }

            for (const [from, to, keys] of runs) {
                if (this.areCycleConsecutive(from.digit, to.digit)) continue;
                bad.push({ digits: [from.digit, to.digit], segments: this.segmentsAlong(keys) });
            }
        }

        return bad;
    }

    /** Segments on a section joining two non-consecutive digits, drawn amber. */
    getInvalidSegments(): Set<string> {
        const invalid = new Set<string>();
        for (const join of this.findBadJoins()) {
            for (const seg of join.segments) invalid.add(seg);
        }
        return invalid;
    }

    /**
     * Check if a line segment from center of cell A to center of cell B
     * passes through the given cell C.
     */
    /**
     * Get the 2 cells that a knight move crosses (excluding start and end).
     * For a 2×1 move: crosses 2 cells at the middle row.
     * For a 1×2 move: crosses 2 cells at the middle column.
     */
    private getCrossedCells(from: Position, to: Position): Position[] {
        const dr = to.row - from.row;
        const dc = to.col - from.col;

        if (Math.abs(dr) === 2) {
            // 2×1 move: crossed cells at the middle row
            const midRow = from.row + Math.sign(dr);
            return [
                { row: midRow, col: from.col },
                { row: midRow, col: to.col }
            ];
        } else {
            // 1×2 move: crossed cells at the middle column
            const midCol = from.col + Math.sign(dc);
            return [
                { row: from.row, col: midCol },
                { row: to.row, col: midCol }
            ];
        }
    }

    /**
     * Check if a knight move crosses through any digit cell.
     */
    protected movesCrossesDigit(from: Position, to: Position): boolean {
        return this.getCrossedCells(from, to).some(
            cell => this.grid.getDigitAt(cell) !== null
        );
    }

    /**
     * Check if two positions are a valid knight move apart.
     */
    protected isKnightMove(from: Position, to: Position): boolean {
        const dr = Math.abs(to.row - from.row);
        const dc = Math.abs(to.col - from.col);
        return (dr === 2 && dc === 1) || (dr === 1 && dc === 2);
    }

    /**
     * Get all accessible cells from the selected cell.
     * Accessible = valid knight move + doesn't cross digits + valid digit constraint.
     */
    getAccessibleCells(): Position[] {
        if (!this.selectedCell) return [];

        const from = this.selectedCell;
        const accessible: Position[] = [];
        const connectedCells = this.getConnectedCells(from);
        const connectedKeys = new Set(connectedCells.map(positionKey));
        const connectionCount = connectedCells.length;

        // If already has 2 connections, only backtracking is allowed
        const canExtend = connectionCount < 2;

        // Get the valid next digits this endpoint can reach (based on direction)
        const nextDigitsForThis = canExtend ? this.getNextDigitsForEndpoint(from) : [];

        for (const [dr, dc] of KNIGHT_MOVES) {
            const to: Position = {
                row: from.row + dr,
                col: from.col + dc
            };

            if (!this.grid.isValidPosition(to)) continue;
            if (this.movesCrossesDigit(from, to)) continue;

            const toKey = positionKey(to);

            // Connected cells are always accessible (for backtracking)
            if (connectedKeys.has(toKey)) {
                accessible.push(to);
                continue;
            }

            // If we can't extend (already 2 connections), skip all other cases
            if (!canExtend) continue;

            // Visited cells that are endpoints can be joined (to connect path sections)
            if (this.visitedCells.has(toKey)) {
                const toConnections = this.getConnectedCells(to);
                // Only allow joining to endpoints (0 or 1 connections)
                if (toConnections.length <= 1) {
                    accessible.push(to);
                }
                continue;
            }

            // Check digit constraints for new cells
            const digitAtTo = this.grid.getDigitAt(to);
            if (digitAtTo !== null) {
                // If this endpoint has valid next digits, only allow those
                if (nextDigitsForThis.length > 0) {
                    if (!nextDigitsForThis.includes(digitAtTo)) continue;
                } else {
                    // No direction yet - only allow min or max digit (start of chain)
                    if (digitAtTo !== this.grid.minDigit && digitAtTo !== this.grid.maxDigit) continue;
                }
            }

            accessible.push(to);
        }

        return accessible;
    }

    /**
     * Select a cell. Does not add to visitedCells until a segment is created.
     */
    selectCell(pos: Position): void {
        if (!this.grid.isValidPosition(pos)) return;
        this.selectedCell = pos;
    }

    /**
     * Remove all segments touching a given cell.
     * Returns true if any segments were removed.
     */
    removeSegmentsAtCell(pos: Position): boolean {
        const key = positionKey(pos);
        const segmentsToRemove: string[] = [];

        for (const seg of this.segments) {
            const [a, b] = parseSegmentKey(seg);
            if (positionKey(a) === key || positionKey(b) === key) {
                segmentsToRemove.push(seg);
            }
        }

        if (segmentsToRemove.length === 0) return false;

        for (const seg of segmentsToRemove) {
            this.segments.delete(seg);
        }

        this.rebuildState();

        if (this.selectedCell && positionKey(this.selectedCell) === key) {
            this.selectedCell = null;
        }

        return true;
    }

    /**
     * Deselect the current cell and remove all segments touching it.
     */
    deselectAndRemoveSegments(): void {
        if (!this.selectedCell) return;

        const key = positionKey(this.selectedCell);
        const segmentsToRemove: string[] = [];

        // Find all segments touching the selected cell
        for (const seg of this.segments) {
            const [a, b] = parseSegmentKey(seg);
            if (positionKey(a) === key || positionKey(b) === key) {
                segmentsToRemove.push(seg);
            }
        }

        // Remove segments
        for (const seg of segmentsToRemove) {
            this.segments.delete(seg);
        }

        // Rebuild visited cells and digits reached
        this.rebuildState();

        this.selectedCell = null;
    }

    /**
     * Create a segment between two cells.
     */
    createSegment(from: Position, to: Position): boolean {
        if (!this.isKnightMove(from, to)) return false;
        if (this.movesCrossesDigit(from, to)) return false;

        const key = segmentKey(from, to);
        if (this.segments.has(key)) return false;

        this.segments.add(key);

        // Add both cells to visited
        this.visitedCells.add(positionKey(from));
        this.visitedCells.add(positionKey(to));

        // Update digits reached (add any digit on these cells)
        const digitFrom = this.grid.getDigitAt(from);
        const digitTo = this.grid.getDigitAt(to);

        if (digitFrom !== null) {
            this.digitsReached.add(digitFrom);
        }
        if (digitTo !== null) {
            this.digitsReached.add(digitTo);
        }

        return true;
    }

    /**
     * Remove a segment between two cells.
     */
    removeSegment(from: Position, to: Position): boolean {
        const key = segmentKey(from, to);
        if (!this.segments.has(key)) return false;

        this.segments.delete(key);
        this.rebuildState();

        return true;
    }

    /**
     * Rebuild visitedCells and digitsReached from segments.
     */
    protected rebuildState(): void {
        this.visitedCells.clear();
        this.digitsReached.clear();

        // Collect all visited cells from segments
        for (const seg of this.segments) {
            const [a, b] = parseSegmentKey(seg);
            this.visitedCells.add(positionKey(a));
            this.visitedCells.add(positionKey(b));
        }

        // Collect all digits that are on visited cells
        for (const key of this.visitedCells) {
            const [row, col] = key.split(',').map(Number);
            const digit = this.grid.getDigitAt({ row, col });
            if (digit !== null) {
                this.digitsReached.add(digit);
            }
        }
    }

    /**
     * Handle a click on a cell. Returns true if state changed.
     */
    handleClick(pos: Position): boolean {
        if (!this.grid.isValidPosition(pos)) return false;

        const posKey = positionKey(pos);
        const selected = this.selectedCell;

        // Case 1: No cell selected - select the clicked cell
        if (!selected) {
            this.selectCell(pos);
            return true;
        }

        const selectedKey = positionKey(selected);

        // Case 2: Clicked the selected cell - deselect and remove segments
        if (posKey === selectedKey) {
            this.deselectAndRemoveSegments();
            return true;
        }

        // Check if clicked cell is connected to selected via segment
        const connectedCells = this.getConnectedCells(selected);
        const isConnected = connectedCells.some(c => positionKey(c) === posKey);

        // Case 3: Clicked a connected cell - backtrack (remove segment, select that cell)
        if (isConnected) {
            this.removeSegment(selected, pos);
            this.selectedCell = pos;
            // Ensure the cell is still in visited (it might have been if it has other connections)
            if (this.getConnectedCells(pos).length > 0 || this.segments.size === 0) {
                this.visitedCells.add(posKey);
            }
            return true;
        }

        // Case 4: Check if it's an accessible cell for new segment (including joining endpoints)
        const accessible = this.getAccessibleCells();
        const isAccessible = accessible.some(c => positionKey(c) === posKey);

        if (isAccessible) {
            this.createSegment(selected, pos);
            this.selectedCell = pos;
            return true;
        }

        // Case 5: Clicked a visited cell (not accessible) - switch focus
        if (this.visitedCells.has(posKey)) {
            this.selectedCell = pos;
            return true;
        }

        // Case 6: Click any other cell - start a new disconnected section
        this.selectCell(pos);
        return true;
    }

    /**
     * Reconstruct an ordered path from segments, if possible.
     * Returns null if segments don't form a valid path/loop.
     */
    reconstructPath(): Position[] | null {
        if (this.segments.size === 0) return null;

        const adjacency = this.buildAdjacencyMap();

        // Find endpoints (degree 1) or any node for a loop
        const endpoints: string[] = [];
        for (const [node, neighbors] of adjacency) {
            if (neighbors.length === 1) {
                endpoints.push(node);
            }
        }

        // If no endpoints, it's a loop - start anywhere
        // If 2 endpoints, start from one
        // If more than 2 endpoints, path is disconnected
        let startKey: string;
        if (endpoints.length === 0) {
            // Loop - start from first node
            startKey = adjacency.keys().next().value!;
        } else if (endpoints.length === 2) {
            startKey = endpoints[0];
        } else {
            // Disconnected or branching - not a valid single path
            return null;
        }

        // Traverse the path
        const path: Position[] = [];
        const visited = new Set<string>();
        let current = startKey;

        while (current && !visited.has(current)) {
            visited.add(current);
            const [row, col] = current.split(',').map(Number);
            path.push({ row, col });

            const neighbors = adjacency.get(current) || [];
            const next = neighbors.find(n => !visited.has(n));
            current = next!;
        }

        return path;
    }

    /**
     * Check if the path forms a closed loop.
     */
    isLoopComplete(): boolean {
        if (this.segments.size === 0) return false;

        const adjacency = this.buildAdjacencyMap();

        // All nodes must have exactly 2 connections for a loop
        for (const neighbors of adjacency.values()) {
            if (neighbors.length !== 2) return false;
        }

        return true;
    }

    /**
     * Check if the puzzle is solved.
     * A valid win requires a closed loop where digits appear in cyclic order
     * (either increasing 1→2→...→9→1 or decreasing 9→8→...→1→9).
     */
    checkWin(): boolean {
        // Must be a closed loop
        if (!this.isLoopComplete()) return false;

        // Must have reached all digits
        if (this.digitsReached.size !== this.grid.digitCount) return false;

        // Reconstruct the path
        const path = this.reconstructPath();
        if (!path) return false;

        // Extract the sequence of digits from the path
        const digitSequence: number[] = [];
        for (const pos of path) {
            const digit = this.grid.getDigitAt(pos);
            if (digit !== null) {
                digitSequence.push(digit);
            }
        }

        // Must have exactly digitCount digits
        if (digitSequence.length !== this.grid.digitCount) return false;

        // Check if the sequence forms a valid cycle
        // All consecutive pairs (including wrap-around) must have the same step (+1 or -1)
        let direction: number | null = null;
        const digitCount = this.grid.digitCount;

        for (let i = 0; i < digitCount; i++) {
            const current = digitSequence[i];
            const next = digitSequence[(i + 1) % digitCount];

            // Calculate step with wrapping
            let step = next - current;
            if (step === -(digitCount - 1)) step = 1;  // max→min is +1
            if (step === digitCount - 1) step = -1;    // min→max is -1

            // Step must be exactly +1 or -1
            if (step !== 1 && step !== -1) return false;

            // All steps must have the same direction
            if (direction === null) {
                direction = step;
            } else if (step !== direction) {
                return false;
            }
        }

        return true;
    }

    getScore(): ScoreBreakdown | null {
        if (!this.checkWin()) return null;

        const stepCount = this.segments.size;
        const crossings = this.countSegmentCrossings();

        return calculateScore(stepCount, crossings);
    }

    /** Scoring-rules overlay: segment crossings (secondary, blue dots). Steps (primary)
     *  are redrawn in amber by the renderer. */
    getScoringHighlight(): ScoringHighlight {
        return { secondaryPoints: this.getCrossingPoints() };
    }

    /** Points where two knight segments cross, in grid-point coords (cell centers
     *  sit at +0.5). */
    private getCrossingPoints(): Position[] {
        const segs = Array.from(this.segments).map(parseSegmentKey);
        const points: Position[] = [];
        for (let i = 0; i < segs.length; i++) {
            for (let j = i + 1; j < segs.length; j++) {
                if (!this.segmentsIntersect(segs[i], segs[j])) continue;
                const p = this.segmentIntersectionPoint(segs[i], segs[j]);
                if (p) points.push({ row: p.row + 0.5, col: p.col + 0.5 });
            }
        }
        return points;
    }

    /** Intersection of two segments in cell-index space, or null if parallel. */
    private segmentIntersectionPoint(seg1: [Position, Position], seg2: [Position, Position]): Position | null {
        const [a, b] = seg1;
        const [c, d] = seg2;
        const rX = b.col - a.col, rY = b.row - a.row;
        const sX = d.col - c.col, sY = d.row - c.row;
        const denom = rX * sY - rY * sX;
        if (denom === 0) return null;
        const t = ((c.col - a.col) * sY - (c.row - a.row) * sX) / denom;
        return { row: a.row + t * rY, col: a.col + t * rX };
    }

    /**
     * Count how many pairs of knight segments cross each other.
     */
    private countSegmentCrossings(): number {
        const segmentList = Array.from(this.segments).map(parseSegmentKey);
        let crossings = 0;

        for (let i = 0; i < segmentList.length; i++) {
            for (let j = i + 1; j < segmentList.length; j++) {
                if (this.segmentsIntersect(segmentList[i], segmentList[j])) {
                    crossings++;
                }
            }
        }

        return crossings;
    }

    /**
     * Check if two line segments (from cell centers) intersect.
     */
    private segmentsIntersect(seg1: [Position, Position], seg2: [Position, Position]): boolean {
        const [a, b] = seg1;
        const [c, d] = seg2;

        // Skip if segments share an endpoint
        if ((a.row === c.row && a.col === c.col) ||
            (a.row === d.row && a.col === d.col) ||
            (b.row === c.row && b.col === c.col) ||
            (b.row === d.row && b.col === d.col)) {
            return false;
        }

        // Line segment intersection using cross products
        const ccw = (p1: Position, p2: Position, p3: Position): number => {
            return (p2.col - p1.col) * (p3.row - p1.row) - (p2.row - p1.row) * (p3.col - p1.col);
        };

        const d1 = ccw(c, d, a);
        const d2 = ccw(c, d, b);
        const d3 = ccw(a, b, c);
        const d4 = ccw(a, b, d);

        if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
            ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
            return true;
        }

        return false;
    }

    /**
     * Reset the game.
     */
    reset(): void {
        this.selectedCell = null;
        this.segments.clear();
        this.visitedCells.clear();
        this.digitsReached.clear();
    }

    /**
     * Jumpstart a solution: place a knight path joining digit 1 to digit 2.
     * Leaves an open chain (it won't close the loop, so the puzzle isn't won)
     * that demonstrates how knight moves connect digits.
     */
    autoStart(): boolean {
        if (this.isStarted()) return false;

        const d1 = this.grid.getDigitPosition(this.grid.minDigit);
        const d2 = this.grid.getDigitPosition(this.grid.minDigit + 1);
        if (!d1 || !d2) return false;

        const path = this.findKnightPath(d1, d2);
        if (!path || path.length < 2) return false;

        for (let i = 0; i + 1 < path.length; i++) {
            this.createSegment(path[i], path[i + 1]);
        }
        return true;
    }

    /**
     * BFS over the knight-move graph from start to goal. Skips moves whose body
     * crosses a digit, and never lands on a digit cell other than the goal, so
     * the path touches only the two endpoint digits. Null if unreachable.
     */
    private findKnightPath(start: Position, goal: Position): Position[] | null {
        const startKey = positionKey(start);
        const goalKey = positionKey(goal);
        const prev = new Map<string, string | null>();
        prev.set(startKey, null);
        const queue: Position[] = [start];

        while (queue.length > 0) {
            const cur = queue.shift()!;
            const curKey = positionKey(cur);

            if (curKey === goalKey) {
                const path: Position[] = [];
                let k: string | null = curKey;
                while (k) {
                    path.push(parsePositionKey(k));
                    k = prev.get(k) ?? null;
                }
                return path.reverse();
            }

            for (const [dr, dc] of KNIGHT_MOVES) {
                const to: Position = { row: cur.row + dr, col: cur.col + dc };
                if (!this.grid.isValidPosition(to)) continue;
                const toKey = positionKey(to);
                if (prev.has(toKey)) continue;
                if (this.movesCrossesDigit(cur, to)) continue;
                if (toKey !== goalKey && this.grid.getDigitAt(to) !== null) continue;
                prev.set(toKey, curKey);
                queue.push(to);
            }
        }

        return null;
    }

    /** " Reach digit 3 next." for the chain ending at this endpoint, if it has one. */
    private nextDigitHint(endpoint: Position): string {
        const digits = this.getNextDigitsForEndpoint(endpoint);
        if (digits.length === 0) return '';
        return ` Reach digit ${digits.join(' or ')} next.`;
    }

    /** Accessible cells that extend the path, i.e. excluding backtrack targets. */
    private getExtensionCells(): Position[] {
        if (!this.selectedCell) return [];
        const connected = new Set(this.getConnectedCells(this.selectedCell).map(positionKey));
        return this.getAccessibleCells().filter(pos => !connected.has(positionKey(pos)));
    }

    getStatus(): string {
        const boldRed = (text: string) =>
            `<span style="color:#e94560;font-weight:bold">${text}</span>`;

        if (!this.isStarted() && !this.selectedCell) {
            return 'Click any cell to start building the knight\'s path.';
        }

        if (this.checkWin()) {
            return 'You win! The loop is complete!';
        }

        const badJoins = this.findBadJoins();
        if (badJoins.length > 0) {
            const sections = badJoins.length > 1 ? ` (${badJoins.length} sections)` : '';
            const [a, b] = badJoins[0].digits;
            return `${boldRed('Warning')}: an amber section joins digits ${a} and ${b}, which are not consecutive${sections}.`;
        }

        if (this.selectedCell) {
            const connections = this.getConnectedCells(this.selectedCell).length;

            if (connections >= 2) {
                return 'Click amber cells to remove a segment, or click the cell again to remove both.';
            }

            const canExtend = this.getExtensionCells().length > 0;
            const nextHint = this.nextDigitHint(this.selectedCell);

            if (connections === 0) {
                return canExtend
                    ? `Extend the path by clicking teal cells.${nextHint}`
                    : 'No knight move leaves this cell, click another cell.';
            }

            if (this.digitsReached.size >= this.grid.digitCount) {
                return canExtend
                    ? 'All digits reached! Close the loop by clicking teal cells.'
                    : 'No way to close the loop from here, backtrack by clicking the amber cell.';
            }

            return canExtend
                ? `Extend the path by clicking teal cells, or backtrack to the amber cell.${nextHint}`
                : `No way to extend the path, backtrack by clicking the amber cell.${nextHint}`;
        }

        return 'Click a path cell to continue, or any other cell to start a new section.';
    }

    // Legacy methods for compatibility
    getPath(): Position[] {
        return this.reconstructPath() || [];
    }

    getCurrentPosition(): Position | null {
        return this.selectedCell;
    }

    getStartPosition(): Position | null {
        const path = this.reconstructPath();
        return path && path.length > 0 ? path[0] : null;
    }

    getValidMoves(): Position[] {
        return this.getAccessibleCells();
    }

    /**
     * Serialize game state to string format.
     * Format: "A0-B2,B2-C4,C4-D6" - comma-separated segment pairs
     */
    serialize(): string {
        const entries: string[] = [];
        for (const seg of this.segments) {
            const [a, b] = parseSegmentKey(seg);
            entries.push(`${this.gs.serializePosition(a)}${this.gs.serializePosition(b)}`);
        }
        return entries.join('');
    }

    /**
     * Deserialize game state from string format.
     */
    deserialize(serialized: string): void {
        this.reset();
        if (!serialized) return;

        for (const entry of splitEntries(serialized, 4)) {
            try {
                const from = this.gs.deserializePosition(entry.slice(0, 2));
                const to = this.gs.deserializePosition(entry.slice(2, 4));

                if (this.grid.isValidPosition(from) && this.grid.isValidPosition(to)) {
                    this.createSegment(from, to);
                }
            } catch {
                // Invalid position format, skip
            }
        }
    }
}
